import axios from 'axios';
import { UpdateOperation, EntityType, ProductSkuConfig } from './types';
import { apiService, ApiService } from '../../services/api';
import { getConfig } from '../../config/runtimeConfig';
import { sanitizeTextInput } from '../../utils/utils';

// Lazy getter — getConfig() is safe to call only after loadConfig() resolves in index.tsx
const API_BASE_URL = () => getConfig().apiBaseUrl;

export const getAuthToken = (): string => {
  try {
    const tokenStorage = localStorage.getItem('okta-token-storage');
    const { accessToken } = tokenStorage ? JSON.parse(tokenStorage) : { accessToken: null };
    return accessToken?.accessToken || '';
  } catch (error) {
    return '';
  }
};

const getHeaders = (): Record<string, string> => {
  const headers: Record<string, string> = {
    'Content-Type': 'application/json',
    Authorization: `Bearer ${getAuthToken()}`,
  };
  // Participate in the current user-operation audit trail when one is active.
  const operationId = apiService.getActiveOperationId();
  if (operationId) {
    headers[ApiService.REQUEST_ID_HEADER_NAME] = operationId;
  }
  return headers;
};

const sanitizeRecordStrings = (record: Record<string, unknown>): Record<string, unknown> =>
  Object.fromEntries(
    Object.entries(record).map(([key, value]) => [
      key,
      typeof value === 'string' ? sanitizeTextInput(value) : value,
    ]),
  );

export const applyUpdates = async (updates: UpdateOperation[]): Promise<void> => {
  for (const update of updates) {
    // Check if this is a DEPENDENT_SERVICE update (SKU associations)
    if (
      update.entityType === EntityType.DEPENDENT_SERVICE &&
      typeof update.updates === 'object' &&
      update.updates !== null &&
      ('added_sku_ids' in update.updates || 'removed_sku_ids' in update.updates)
    ) {
      await applyDependentServiceUpdates(update);
    } else if (
      // Check if this is an entitlement update (from EntitlementTableEditor)
      update.entityType === EntityType.ENTITLEMENT &&
      typeof update.updates === 'object' &&
      update.updates !== null &&
      'entitlements' in update.updates
    ) {
      await applyEntitlementUpdates(update);
    } else if (
      update.entityType === EntityType.ENTITLEMENT ||
      update.entityType === EntityType.ORG_ENTITLEMENT
    ) {
      await applySingleEntitlementUpdate(update);
    } else if (
    // Check if this is a SKU config update
      (update.entityType === EntityType.SKU_STANDALONE ||
        update.entityType === EntityType.SKU_SUITE ||
        update.entityType === EntityType.SKU_ADDON) &&
      typeof update.updates === 'object' &&
      update.updates !== null &&
      'deleteOperations' in update.updates &&
      'addOperations' in update.updates
    ) {
      await applySKUConfigUpdates({
        ...(update.updates as Parameters<typeof applySKUConfigUpdates>[0]),
        entityType: update.entityType,
      });
    } else if (
      // Regions & Questions update (from RegionsQuestionsEditor)
      update.entityType === EntityType.PRODUCT &&
      typeof update.updates === 'object' &&
      update.updates !== null &&
      'regionsQuestions' in update.updates
    ) {
      await applyRegionsQuestionsUpdates(update);
    } else {
      // Regular entity update
      const key = getConfigKey(update.entityType);
      const endpoint = `${API_BASE_URL()}/config/${key}`;
      const payload = buildUpdatePayload(update);

      try {
        await axios.patch(endpoint, payload, {
          headers: getHeaders(),
        });
      } catch (error) {
        throw error;
      }
    }
  }
};

/**
 * Apply an individual entitlement edit and mirror its shared fields to the
 * matching entitlement in the other table when one exists for the same product.
 */
const applySingleEntitlementUpdate = async (update: UpdateOperation): Promise<void> => {
  const sourceOriginal = update.originalData;
  const sourceValues = { ...sourceOriginal, ...update.updates };
  const sharedFields = ['name', 'value', 'description', 'needs_quantity', 'unit'] as const;
  const hasSharedFieldChange = sharedFields.some((field) =>
    Object.prototype.hasOwnProperty.call(update.updates, field),
  );

  if (!hasSharedFieldChange) {
    await patchSingleEntity(update);
    return;
  }

  const productId = sourceValues.product_id ?? sourceValues.ProductID;
  const sourceId = sourceOriginal.id ?? sourceOriginal.ID ?? update.entityId;
  const normalizeId = (value: unknown) => String(value ?? '');
  const normalizeProductId = (row: Record<string, unknown>) =>
    normalizeId(row.product_id ?? row.ProductID);
  const normalizeSharedFields = (row: Record<string, unknown>) => ({
    name: String(row.name ?? row.Name ?? ''),
    value: String(row.value ?? row.Value ?? ''),
    needs_quantity: Boolean(row.needs_quantity ?? row.NeedsQuantity),
    unit: String(row.unit ?? row.Unit ?? ''),
  });
  // Resolve the counterpart from the pre-edit identity. This still works when
  // the user is changing name, value, quantity, or unit in the same edit.
  const identity = normalizeSharedFields(sourceOriginal);
  const sameIdentity = (row: Record<string, unknown>) => {
    const candidate = normalizeSharedFields(row);
    return normalizeProductId(row) === normalizeId(productId) &&
      candidate.name === identity.name &&
      candidate.value === identity.value &&
      candidate.needs_quantity === identity.needs_quantity &&
      candidate.unit === identity.unit;
  };

  const [allEntitlements, allOrgEntitlements] = await Promise.all([
    apiService.getEntitlements(),
    apiService.getOrgEntitlements(),
  ]);
  const sourceRows = update.entityType === EntityType.ENTITLEMENT
    ? allEntitlements as unknown as Array<Record<string, unknown>>
    : allOrgEntitlements as unknown as Array<Record<string, unknown>>;
  const counterpartRows = update.entityType === EntityType.ENTITLEMENT
    ? allOrgEntitlements as unknown as Array<Record<string, unknown>>
    : allEntitlements as unknown as Array<Record<string, unknown>>;

  // Verify the source still resolves to exactly one record and avoid mirroring
  // when the shared identity is ambiguous in either table.
  const sourceMatches = sourceRows.filter((row) => normalizeId(row.id ?? row.ID) === normalizeId(sourceId));
  const counterpartMatches = counterpartRows.filter(sameIdentity);
  if (sourceMatches.length !== 1 || !sameIdentity(sourceMatches[0]) || counterpartMatches.length !== 1) {
    await patchSingleEntity(update);
    return;
  }

  const sourceTable = update.entityType === EntityType.ENTITLEMENT ? 'entitlements' : 'org_entitlements';
  const counterpartTable = update.entityType === EntityType.ENTITLEMENT ? 'org_entitlements' : 'entitlements';
  const sourceData = toBulkEntitlementData(sourceValues);
  const counterpartData = toBulkEntitlementData({ ...counterpartMatches[0], ...sourceValues });

  if (sourceTable === 'org_entitlements') {
    sourceData.AccessType = sourceValues.access_type ?? sourceValues.AccessType ?? '';
    sourceData.IsService = sourceValues.is_service ?? sourceValues.IsService ?? false;
  }
  if (counterpartTable === 'org_entitlements') {
    counterpartData.AccessType = counterpartMatches[0].access_type ?? counterpartMatches[0].AccessType ?? '';
    counterpartData.IsService = counterpartMatches[0].is_service ?? counterpartMatches[0].IsService ?? false;
  }

  await apiService.bulkUpdate({
    bulk_update: [
      {
        table_key: sourceTable,
        updates: [{ primary_keys: { ID: sourceId }, data: sourceData }],
      },
      {
        table_key: counterpartTable,
        updates: [{
          primary_keys: { ID: counterpartMatches[0].id ?? counterpartMatches[0].ID },
          data: counterpartData,
        }],
      },
    ],
  });
};

const toBulkEntitlementData = (row: Record<string, unknown>): Record<string, unknown> => ({
  Name: sanitizeTextInput(String(row.name ?? row.Name ?? '')),
  Value: sanitizeTextInput(String(row.value ?? row.Value ?? '')),
  Description: sanitizeTextInput(String(row.description ?? row.Description ?? '')),
  NeedsQuantity: row.needs_quantity ?? row.NeedsQuantity ?? false,
  ProductID: row.product_id ?? row.ProductID,
  Unit: sanitizeTextInput(String(row.unit ?? row.Unit ?? '')),
});

const patchSingleEntity = async (update: UpdateOperation): Promise<void> => {
  const key = getConfigKey(update.entityType);
  await axios.patch(`${API_BASE_URL()}/config/${key}`, buildUpdatePayload(update), {
    headers: getHeaders(),
  });
};

/**
 * Apply entitlement changes following ACID principles.
 * All operations (inserts, updates, deletes) are batched and executed
 * in a controlled sequence. If any step fails, previously completed
 * steps are rolled back to maintain data consistency.
 *
 * Execution order: Inserts → Updates → Deletes
 * Rollback order (on failure): reverse of completed steps
 */
const applyEntitlementUpdates = async (update: UpdateOperation): Promise<void> => {
  const updatesData = update.updates as Record<string, unknown>;
  const changedEnts = (updatesData.entitlements as Array<Record<string, unknown>>) || [];
  const changedOrgEnts = (updatesData.orgEntitlements as Array<Record<string, unknown>>) || [];

  // Separate by action
  const entInserts = changedEnts.filter((e) => e.action === 'insert');
  const entUpdates = changedEnts.filter((e) => e.action === 'update');
  const entDeletes = changedEnts.filter((e) => e.action === 'delete');

  const orgEntInserts = changedOrgEnts.filter((e) => e.action === 'insert');
  const orgEntUpdates = changedOrgEnts.filter((e) => e.action === 'update');
  const orgEntDeletes = changedOrgEnts.filter((e) => e.action === 'delete');

  // Track completed steps for rollback
  const completedSteps: string[] = [];

  // Build all payloads upfront (fail fast on data issues before any API calls)
  const bulkInsertPayload = buildEntitlementInsertPayload(entInserts, orgEntInserts);
  const bulkUpdatePayload = buildEntitlementUpdatePayload(entUpdates, orgEntUpdates);
  const deleteIds = {
    entitlements: entDeletes.filter((e) => e.id).map((e) => e.id as number),
    orgEntitlements: orgEntDeletes.filter((e) => e.id).map((e) => e.id as number),
  };

  try {
    // STEP 1: INSERTS — create new records first
    if (bulkInsertPayload) {
      await apiService.bulkInsert(bulkInsertPayload);
      completedSteps.push('insert');
    }

    // STEP 2: UPDATES — modify existing records
    if (bulkUpdatePayload) {
      await apiService.bulkUpdate(bulkUpdatePayload);
      completedSteps.push('update');
    }

    // STEP 3: DELETES — remove records last
    if (deleteIds.entitlements.length > 0 || deleteIds.orgEntitlements.length > 0) {
      for (const id of deleteIds.entitlements) {
        await apiService.deleteEntitlement(id);
      }
      for (const id of deleteIds.orgEntitlements) {
        await apiService.deleteOrgEntitlement(id);
      }
      completedSteps.push('delete');
    }
  } catch (error) {
    // Rollback: attempt to undo completed steps in reverse order
    const rollbackErrors: string[] = [];

    for (const step of [...completedSteps].reverse()) {
      try {
        if (step === 'insert') {
          // Rollback inserts by deleting the newly inserted records
          for (const ent of entInserts) {
            if (ent.id) {
              await apiService.deleteEntitlement(ent.id as number);
            }
          }
          for (const ent of orgEntInserts) {
            if (ent.id) {
              await apiService.deleteOrgEntitlement(ent.id as number);
            }
          }
        }
        if (step === 'update') {
          // Rollback updates by restoring original data
          const originalData = update.originalData as Record<string, unknown>;
          const origEnts = (originalData.entitlements as Array<Record<string, unknown>>) || [];
          const origOrgEnts = (originalData.orgEntitlements as Array<Record<string, unknown>>) || [];

          const rollbackUpdatePayload = buildEntitlementUpdatePayload(
            entUpdates.map((ent) => {
              const orig = origEnts.find((o) => o.id === ent.id);
              return orig ? { ...orig, action: 'update' } : ent;
            }),
            orgEntUpdates.map((ent) => {
              const orig = origOrgEnts.find((o) => o.id === ent.id);
              return orig ? { ...orig, action: 'update' } : ent;
            }),
          );
          if (rollbackUpdatePayload) {
            await apiService.bulkUpdate(rollbackUpdatePayload);
          }
        }
        // Note: delete rollback (re-insert) is not attempted as we don't have
        // full original data for deleted rows in the right insert format
      } catch (rollbackError) {
        rollbackErrors.push(`Failed to rollback ${step}: ${(rollbackError as Error).message}`);
      }
    }

    const baseMessage = `Entitlement update failed: ${(error as Error).message}`;
    if (rollbackErrors.length > 0) {
      throw new Error(`${baseMessage}. Rollback errors: ${rollbackErrors.join('; ')}`);
    }
    throw new Error(baseMessage);
  }
};

/**
 * Apply DEPENDENT_SERVICE updates (SKU org entitlement associations).
 * Handles adding and removing SKU associations.
 */
const applyDependentServiceUpdates = async (update: UpdateOperation): Promise<void> => {
  const updatesData = update.updates as Record<string, unknown>;
  const addedSKUIDs = (updatesData.added_sku_ids as number[]) || [];
  const removedSKUIDs = (updatesData.removed_sku_ids as number[]) || [];
  const orgEntitlementId = updatesData.org_entitlement_id as number;

  try {
    // Step 1: Add new SKU associations (bulk insert)
    if (addedSKUIDs.length > 0) {
      const bulkInsertPayload = {
        bulk_entry: [
          {
            table_key: 'sku_org_entitlement_configs',
            entries: addedSKUIDs.map((skuId) => ({
              data: {
                SkuID: skuId,
                OrgEntitlementID: orgEntitlementId,
              },
            })),
          },
        ],
      };
      await apiService.bulkInsert(bulkInsertPayload);
    }

    // Step 2: Remove SKU associations (individual deletes)
    for (const skuId of removedSKUIDs) {
      await apiService.deleteSKUOrgEntitlementConfig(skuId, orgEntitlementId);
    }
  } catch (error) {
    throw new Error(`Failed to update SKU associations: ${(error as Error).message}`);
  }
};

/** Build bulk_insert payload for entitlements */
const buildEntitlementInsertPayload = (
  entInserts: Array<Record<string, unknown>>,
  orgEntInserts: Array<Record<string, unknown>>,
): Record<string, unknown> | null => {
  const bulkInsertEntries: Array<Record<string, unknown>> = [];

  if (entInserts.length > 0) {
    bulkInsertEntries.push({
      table_key: 'entitlements',
      entries: entInserts.map((ent) => ({
        data: {
          ID: ent.id,
          Name: sanitizeTextInput(String(ent.name ?? '')),
          Value: sanitizeTextInput(String(ent.value ?? '')),
          Description: sanitizeTextInput(String(ent.description || '')),
          NeedsQuantity: ent.needs_quantity ?? false,
          ProductID: ent.product_id,
          Unit: sanitizeTextInput(String(ent.unit || '')),
        },
      })),
    });
  }

  if (orgEntInserts.length > 0) {
    bulkInsertEntries.push({
      table_key: 'org_entitlements',
      entries: orgEntInserts.map((ent) => ({
        data: {
          ID: ent.id,
          Name: sanitizeTextInput(String(ent.name ?? '')),
          Value: sanitizeTextInput(String(ent.value ?? '')),
          Description: sanitizeTextInput(String(ent.description || '')),
          NeedsQuantity: ent.needs_quantity ?? false,
          ProductID: ent.product_id,
          Unit: sanitizeTextInput(String(ent.unit || '')),
          AccessType: ent.access_type || '',
          IsService: ent.is_service ?? false,
        },
      })),
    });
  }

  return bulkInsertEntries.length > 0 ? { bulk_entry: bulkInsertEntries } : null;
};

/** Build bulk_update payload for entitlements */
const buildEntitlementUpdatePayload = (
  entUpdates: Array<Record<string, unknown>>,
  orgEntUpdates: Array<Record<string, unknown>>,
): Record<string, unknown> | null => {
  const bulkUpdateEntries: Array<Record<string, unknown>> = [];

  if (entUpdates.length > 0) {
    bulkUpdateEntries.push({
      table_key: 'entitlements',
      updates: entUpdates.map((ent) => ({
        primary_keys: { ID: ent.id },
        data: {
          Name: sanitizeTextInput(String(ent.name ?? '')),
          Value: sanitizeTextInput(String(ent.value ?? '')),
          Description: sanitizeTextInput(String(ent.description || '')),
          NeedsQuantity: ent.needs_quantity ?? false,
          ProductID: ent.product_id,
          Unit: sanitizeTextInput(String(ent.unit || '')),
        },
      })),
    });
  }

  if (orgEntUpdates.length > 0) {
    bulkUpdateEntries.push({
      table_key: 'org_entitlements',
      updates: orgEntUpdates.map((ent) => ({
        primary_keys: { ID: ent.id },
        data: {
          Name: sanitizeTextInput(String(ent.name ?? '')),
          Value: sanitizeTextInput(String(ent.value ?? '')),
          Description: sanitizeTextInput(String(ent.description || '')),
          NeedsQuantity: ent.needs_quantity ?? false,
          ProductID: ent.product_id,
          Unit: sanitizeTextInput(String(ent.unit || '')),
          AccessType: ent.access_type || '',
          IsService: ent.is_service ?? false,
        },
      })),
    });
  }

  return bulkUpdateEntries.length > 0 ? { bulk_update: bulkUpdateEntries } : null;
};

const applySKUConfigUpdates = async (
  updates: {
    entityType?: string;
    skuId: number;
    deleteOperations: Array<{ ProductID: number; SkuID: number }>;
    addOperations: Array<{
      config: {
        ProductID: number;
        SkuID: number;
        Operation?: string;
        Operand?: number;
        ForceRegionName?: string;
        AllowedRegions?: string;
      };
      entitlements: Array<{
        ProductSkuConfigProductID: number;
        ProductSkuConfigSkuID: number;
        EntitlementID: number;
      }>;
    }>;
  }
): Promise<void> => {
  // STEP 1: DELETE operations - delete from product_sku_configs (CASCADE handles entitlements)
  for (const deleteOp of updates.deleteOperations) {
    await apiService.deleteProductSkuConfig(deleteOp.ProductID, deleteOp.SkuID);
  }

  // STEP 2: POST operations - add to product_sku_configs and product_sku_config_entitlements
  for (const addOp of updates.addOperations) {
    // For addon SKUs, strip AllowedRegions from product_sku_configs payload
    // (AllowedRegions belongs in product_addon_config)
    const { AllowedRegions, ...skuConfigPayload } = sanitizeRecordStrings(addOp.config) as typeof addOp.config;

    // Create product_sku_config
    await apiService.createProductSkuConfig(skuConfigPayload);

    // Create product_sku_config_entitlements for each entitlement
    for (const entitlement of addOp.entitlements) {
      await apiService.createProductSkuConfigEntitlement(entitlement);
    }

    // For addon SKUs, update product_addon_config with AllowedRegions via bulk_update
    // (The product_addon_config row already exists; we patch AllowedRegions in place)
    if (updates.entityType === 'sku_addon' && AllowedRegions) {
      await apiService.bulkUpdate({
        bulk_update: [
          {
            table_key: 'product_addon_config',
            updates: [
              {
                primary_keys: {
                  SkuID: addOp.config.SkuID,
                  BaseProductID: addOp.config.ProductID,
                },
                data: {
                  AllowedRegions,
                },
              },
            ],
          },
        ],
      });
    }
  }
};

/**
 * Apply regions & provisioning questions changes for a product.
 *
 * Strategy:
 *  - Allowed regions: delete all existing rows for the product, then re-insert the selected ones.
 *  - Provisioning questions:
 *      - 'new'     → bulk_insert
 *      - 'existing' (changed fields) → bulk_update
 *      - 'deleted' → DELETE by ID
 */
const applyRegionsQuestionsUpdates = async (update: UpdateOperation): Promise<void> => {
  const productId = Number(update.entityId);
  const payload = (update.updates as Record<string, unknown>).regionsQuestions as {
    allowedRegions: string[];
    questions: Array<{
      id?: number;
      name: string;
      display_name: string;
      description: string;
      type: string;
      _status: 'existing' | 'new' | 'deleted';
    }>;
  };
  const originalData = (update.originalData as Record<string, unknown>).regionsQuestions as {
    allowedRegions: string[];
    questions: typeof payload.questions;
  };

  // ── 1. Allowed Regions ─────────────────────────────────────────────────────
  const regionsChanged =
    JSON.stringify([...payload.allowedRegions].sort()) !==
    JSON.stringify([...(originalData?.allowedRegions ?? [])].sort());

  if (regionsChanged) {
    // Delete each existing allowed region individually (both ProductID + RegionName required)
    for (const regionName of (originalData?.allowedRegions ?? [])) {
      await axios.delete(`${API_BASE_URL()}/config/product_allowed_regions`, {
        headers: getHeaders(),
        data: { ProductID: productId, RegionName: regionName },
      });
    }

    // Re-insert selected regions
    if (payload.allowedRegions.length > 0) {
      const regionEntries = payload.allowedRegions.map((regionName) => ({
        data: {
          ProductID: productId,
          RegionName: regionName,
        },
      }));
      await axios.post(
        `${API_BASE_URL()}/config/bulk_insert`,
        {
          bulk_entry: [
            { table_key: 'product_allowed_regions', entries: regionEntries },
          ],
        },
        { headers: getHeaders() }
      );
    }
  }

  // ── 2. Provisioning Questions ──────────────────────────────────────────────
  const toInsert = payload.questions.filter((q) => q._status === 'new');
  const toUpdate = payload.questions.filter(
    (q) =>
      q._status === 'existing' &&
      q.id !== undefined &&
      (() => {
        const orig = originalData?.questions?.find((oq) => oq.id === q.id);
        return (
          !orig ||
          orig.name !== q.name ||
          orig.display_name !== q.display_name ||
          orig.description !== q.description ||
          orig.type !== q.type
        );
      })()
  );
  const toDelete = payload.questions.filter(
    (q) => q._status === 'deleted' && q.id !== undefined
  );

  // Insert new questions
  if (toInsert.length > 0) {
    // Fetch all provisioning questions to determine the next available ID
    const allQuestions = await apiService.getAllProvisioningQuestions();
    const maxQuestionId = allQuestions.length > 0
      ? Math.max(...allQuestions.map((q) => q.id || 0), 0)
      : 0;

    const insertEntries = toInsert.map((q, idx) => ({
      data: {
        ID: maxQuestionId + idx + 1,
        ProductID: productId,
        Name: q.name,
        DisplayName: q.display_name,
        Description: q.description,
        Type: q.type,
      },
    }));
    await axios.post(
      `${API_BASE_URL()}/config/bulk_insert`,
      {
        bulk_entry: [
          { table_key: 'provisioning_questions', entries: insertEntries },
        ],
      },
      { headers: getHeaders() }
    );
  }

  // Update changed existing questions
  for (const q of toUpdate) {
    await axios.patch(
      `${API_BASE_URL()}/config/provisioning_questions`,
      {
        primary_keys: { ID: q.id },
        data: {
          Name: q.name,
          DisplayName: q.display_name,
          Description: q.description,
          Type: q.type,
        },
      },
      { headers: getHeaders() }
    );
  }

  // Delete removed questions
  for (const q of toDelete) {
    await axios.delete(`${API_BASE_URL()}/config/provisioning_questions`, {
      headers: getHeaders(),
      data: { ID: q.id },
    });
  }
};

export const deleteProductSkuConfig = async (productId: string): Promise<void> => {
  const endpoint = `${API_BASE_URL()}/config/product_sku`;

  try {
    await axios.delete(endpoint, {
      headers: getHeaders(),
      data: {
        ID: productId,
      },
    });
  } catch (error) {
    throw error;
  }
};

const getConfigKey = (entityType: EntityType): string => {
  switch (entityType) {
    case EntityType.PRODUCT:
      return 'products';
    case EntityType.ENTITLEMENT:
      return 'entitlements';
    case EntityType.ORG_ENTITLEMENT:
      return 'org_entitlements';
    case EntityType.SKU_STANDALONE:
    case EntityType.SKU_SUITE:
    case EntityType.SKU_ADDON:
      return 'skus';
    default:
      throw new Error(`Unknown entity type: ${entityType}`);
  }
};

const buildUpdatePayload = (update: UpdateOperation): Record<string, unknown> => {
  return {
    primary_keys: {
      ID: update.entityId,
    },
    data: sanitizeRecordStrings(update.updates),
  };
};

export const fetchEntities = async <T,>(entityType: EntityType): Promise<T[]> => {
  const key = getConfigKey(entityType);
  const endpoint = `${API_BASE_URL()}/config/${key}`;

  try {
    const response = await axios.get(endpoint, {
      headers: getHeaders(),
    });
    return response.data.data || response.data;
  } catch (error) {
    throw error;
  }
};

export const fetchEntityById = async <T,>(
  entityType: EntityType,
  entityId: string
): Promise<T> => {
  const key = getConfigKey(entityType);
  const endpoint = `${API_BASE_URL()}/config/${key}`;

  try {
    const response = await axios.get(endpoint, {
      headers: getHeaders(),
      data: {
        ID: entityId,
      },
    });
    return response.data;
  } catch (error) {
    throw error;
  }
};

export const fetchProductSkuConfig = async (productId: string): Promise<ProductSkuConfig> => {
  const endpoint = `${API_BASE_URL()}/config/product_sku`;

  try {
    const response = await axios.get(endpoint, {
      headers: getHeaders(),
      data: {
        ID: productId,
      },
    });
    return response.data;
  } catch (error) {
    throw error;
  }
};

export const extractChanges = (
  originalData: Record<string, unknown>,
  updatedData: Record<string, unknown>
): Record<string, unknown> => {
  const changes: Record<string, unknown> = {};

  Object.keys(updatedData).forEach((key) => {
    if (updatedData[key] !== originalData[key]) {
      changes[key] = updatedData[key];
    }
  });

  return changes;
};
